import { useEffect } from "react";
import { useConfigContext } from "../../../ConfigProvider.tsx";
import { useAutocomplete } from "../../providers/AutocompleteProvider.tsx";

function startAnimatedPlaceholderRotation(
    inputEl: HTMLInputElement | null,
    texts: string[],
    intervalMs = 4000,
    animDurationMs = 750,
    searchPlaceholder: string,
): ReturnType<typeof setInterval> | undefined {
    if (!inputEl || !texts.length) return;
    const wrapper = inputEl.closest('.algoliaAutocompleteForm__searchBar');
    if (!wrapper) return;
    const container = wrapper.querySelector<HTMLDivElement>('.placeholder-container')!;
    container.innerHTML = '';
    inputEl.placeholder = '';
    let idx = 0;

    let current = document.createElement('span');
    current.className = 'placeholder-text current';
    current.innerHTML = '<span>' + searchPlaceholder + '</span> ' + texts[idx];
    container.appendChild(current);

    function rotate() {
        container.querySelectorAll<HTMLSpanElement>('.placeholder-text:not(.current)')
            .forEach((s) => container.removeChild(s));

        const nextIdx = (idx + 1) % texts.length;
        const next = document.createElement('span');
        next.className = 'placeholder-text';
        next.innerHTML = '<span>' + searchPlaceholder + '</span> ' + texts[nextIdx];
        container.appendChild(next);

        void next.getBoundingClientRect();
        current.classList.add('slide-out');
        next.classList.add('slide-in');

        setTimeout(() => {
            if (current.parentNode) container.removeChild(current);
            next.classList.remove('slide-in');
            next.classList.add('current');
            current = next;
            idx = nextIdx;
        }, animDurationMs);
    }

    rotate();
    return setInterval(rotate, intervalMs);
}

export function Form() {
    const {
        inputRef,
        formRef,
        autocomplete,
        isModalOpen,
        setIsModalOpen,
        autocompleteState: { query },
    } = useAutocomplete();
    const { tc } = useConfigContext();

    useEffect(() => {
        if (isModalOpen && inputRef?.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
        }
    }, [isModalOpen, inputRef]);

    useEffect(() => {
        const placeholders = [
            tc.search_placeholder_1,
            tc.search_placeholder_2,
            tc.search_placeholder_3,
        ];
        let rotator: ReturnType<typeof setInterval> | undefined;

        const startRotator = () => {
            if (inputRef.current && !rotator) {
                rotator = startAnimatedPlaceholderRotation(
                    inputRef.current,
                    placeholders,
                    4000,
                    750,
                    tc.search_placeholder
                );
            }
        };

        const stopRotator = () => {
            if (rotator) {
                clearInterval(rotator as unknown as number);
                rotator = undefined;
            }
        };

        const inputEl = inputRef.current;
        if (inputEl) {
            inputEl.addEventListener('focus', stopRotator);
            inputEl.addEventListener('blur', startRotator);

            if (document.activeElement !== inputEl) {
                startRotator();
            }
        }

        return () => {
            stopRotator();
            if (inputEl) {
                inputEl.removeEventListener('focus', stopRotator);
                inputEl.removeEventListener('blur', startRotator);
            }
        };
    }, [inputRef, tc.search_placeholder, tc.search_placeholder_1, tc.search_placeholder_2, tc.search_placeholder_3]);

    const originalFormProps = autocomplete.getFormProps({ inputElement: inputRef.current });
    const originalInputProps = autocomplete.getInputProps({ inputElement: inputRef.current });

    useEffect(() => {
        const hasQuery = query?.trim().length > 0;
        const currentIsModalOpen = isModalOpen;

        if (hasQuery && !currentIsModalOpen) {
            setIsModalOpen(true);
            autocomplete.setIsOpen(true);
        } else if (!hasQuery && currentIsModalOpen) {
            setIsModalOpen(false);
            autocomplete.setIsOpen(false);
        }

        const inputEl = inputRef.current;
        const wrapper = inputEl?.closest('.algoliaAutocompleteForm__searchBar');
        if (inputEl && wrapper) {
            if (hasQuery) {
                wrapper.classList.add('is-filled');
            } else {
                wrapper.classList.remove('is-filled');
            }
        }
    }, [query, setIsModalOpen, autocomplete, inputRef, isModalOpen]);

    const onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        originalInputProps.onKeyDown?.(e);
        if (e.key === "Escape") {
            setIsModalOpen(false);
            autocomplete.setIsOpen(false);
        }
    };

    useEffect(() => {
        const inputEl = inputRef.current;
        const wrapper = inputEl?.closest('.algoliaAutocompleteForm__searchBar');
        if (!inputEl || !wrapper) return;

        const updateFilledClass = () => {
            if (inputEl.value.trim()) {
                wrapper.classList.add('is-filled');
            } else {
                wrapper.classList.remove('is-filled');
            }
        };

        const handleFocus = () => wrapper.classList.add('is-focus');
        const handleBlur = () => wrapper.classList.remove('is-focus');

        inputEl.addEventListener('input', updateFilledClass);
        inputEl.addEventListener('focus', handleFocus);
        inputEl.addEventListener('blur', handleBlur);

        updateFilledClass();

        return () => {
            inputEl.removeEventListener('input', updateFilledClass);
            inputEl.removeEventListener('focus', handleFocus);
            inputEl.removeEventListener('blur', handleBlur);
        };
    }, [inputRef]);

    return (
        <form
            ref={formRef}
            className="algoliaAutocompleteForm"
            {...originalFormProps}
        >
            <div className="algoliaAutocompleteForm__searchBar">
                <input
                    ref={inputRef}
                    className="algoliaAutocompleteForm__input"
                    {...originalInputProps}
                    onKeyDown={onInputKeyDown}
                    autoComplete="off"
                />
                <div className="placeholder-container"></div>
                <label className="algoliaAutocompleteForm__label" {...autocomplete.getLabelProps({})}>
                    <button className="algoliaAutocompleteForm__submit" type="submit">
                        <span className="sr-only">{tc.search}</span>
                        <i className="iconNew iconNew-search" aria-hidden="true"></i>
                    </button>
                </label>
            </div>

            <small className="algoliaAutocompleteForm__legend">{tc.searchHint}</small>
        </form>
    );
}